import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Chip, Alert, Snackbar, Tooltip, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Divider
} from '@mui/material';
import {
  Refresh as RefreshIcon, Replay as RetryIcon, Cancel as CancelIcon, Email as EmailIcon,
  CheckCircle as CheckCircleIcon, Error as ErrorIcon, Schedule as ScheduleIcon,
  BarChart as BarChartIcon, Delete as DeleteIcon, FilterList as FilterIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import EmailQueueService from '../../services/emailQueue.service';

const EmailQueueDashboard = () => {
  const { hasPermission } = useAuth();
  const [emails, setEmails] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [cleanupDialog, setCleanupDialog] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(30);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    client_id: '',
    from_date: '',
    to_date: ''
  });

  useEffect(() => {
    fetchData();
    fetchStatistics();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await EmailQueueService.getAll(filters);
      const rows = response.data?.data ?? response.data ?? [];
      setEmails(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Error loading emails:', error);
      showSnackbar('Error loading emails', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await EmailQueueService.getStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    await fetchStatistics();
    setRefreshing(false);
    showSnackbar('Data refreshed');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleRetry = async (emailId) => {
    try {
      await EmailQueueService.retry(emailId);
      showSnackbar('Email queued for retry');
      fetchData();
    } catch (error) {
      console.error('Error retrying email:', error);
      showSnackbar('Error retrying email', 'error');
    }
  };

  const handleCancel = async (emailId) => {
    if (window.confirm('Are you sure you want to cancel this email?')) {
      try {
        await EmailQueueService.cancel(emailId);
        showSnackbar('Email cancelled');
        fetchData();
      } catch (error) {
        console.error('Error cancelling email:', error);
        showSnackbar('Error cancelling email', 'error');
      }
    }
  };

  const handleCleanup = async () => {
    try {
      await EmailQueueService.cleanup(cleanupDays);
      showSnackbar(`Cleaned up emails older than ${cleanupDays} days`);
      setCleanupDialog(false);
      fetchData();
      fetchStatistics();
    } catch (error) {
      console.error('Error cleaning up emails:', error);
      showSnackbar('Error cleaning up emails', 'error');
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    fetchData();
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      sent: 'success',
      failed: 'error',
      cancelled: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <ScheduleIcon />,
      sent: <CheckCircleIcon />,
      failed: <ErrorIcon />,
      cancelled: <CancelIcon />
    };
    return icons[status] || <EmailIcon />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading email queue...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon color="primary" />
                  <Typography variant="h6">Total Emails</Typography>
                </Box>
                <Typography variant="h4">{statistics.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <ScheduleIcon color="warning" />
                  <Typography variant="h6">Pending</Typography>
                </Box>
                <Typography variant="h4">{statistics.pending}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="h6">Sent</Typography>
                </Box>
                <Typography variant="h4">{statistics.sent}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <ErrorIcon color="error" />
                  <Typography variant="h6">Failed</Typography>
                </Box>
                <Typography variant="h4">{statistics.failed}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Main Content */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" component="h1">
              Email Queue Dashboard
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={refreshing}
              >
                Refresh
              </Button>
              {hasPermission('emails.queue.manage') && (
                <Button
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={() => setCleanupDialog(true)}
                  color="warning"
                >
                  Cleanup
                </Button>
              )}
            </Box>
          </Box>

          {/* Filters */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="sent">Sent</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="From Date"
                    type="date"
                    value={filters.from_date}
                    onChange={(e) => handleFilterChange('from_date', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="To Date"
                    type="date"
                    value={filters.to_date}
                    onChange={(e) => handleFilterChange('to_date', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="contained"
                    onClick={applyFilters}
                    fullWidth
                    sx={{ height: '56px' }}
                  >
                    Apply Filters
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Email Queue Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Email Address</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Scheduled</TableCell>
                  <TableCell>Sent</TableCell>
                  <TableCell>Attempts</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emails.map((email) => (
                  <TableRow key={email.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getStatusIcon(email.status)}
                        <Chip
                          label={email.status}
                          size="small"
                          color={getStatusColor(email.status)}
                          variant="outlined"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {email.client?.name || 'Unknown Client'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {email.email_address}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {email.template?.name || 'Default Template'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(email.scheduled_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {email.sent_at ? formatDate(email.sent_at) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {email.attempts}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1}>
                        {email.status === 'failed' && hasPermission('emails.queue.manage') && (
                          <Tooltip title="Retry email">
                            <IconButton
                              size="small"
                              onClick={() => handleRetry(email.id)}
                            >
                              <RetryIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {email.status === 'pending' && hasPermission('emails.queue.manage') && (
                          <Tooltip title="Cancel email">
                            <IconButton
                              size="small"
                              onClick={() => handleCancel(email.id)}
                              color="error"
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {emails.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box py={4}>
                        <EmailIcon color="disabled" sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No emails found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          No emails match your current filters
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Cleanup Dialog */}
      <Dialog open={cleanupDialog} onClose={() => setCleanupDialog(false)}>
        <DialogTitle>Cleanup Old Emails</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            This will permanently delete sent emails older than the specified number of days.
          </Typography>
          <TextField
            label="Days"
            type="number"
            value={cleanupDays}
            onChange={(e) => setCleanupDays(parseInt(e.target.value))}
            fullWidth
            margin="normal"
            inputProps={{ min: 1, max: 365 }}
            helperText="Enter number of days (1-365)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCleanupDialog(false)}>Cancel</Button>
          <Button onClick={handleCleanup} variant="contained" color="warning">
            Cleanup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailQueueDashboard;
